import { parseUtcTimestamp } from './timeUtils';
import type { PlanningMeasure } from '../types/scenario';

const formatTs = (iso?: string) =>
  iso ? parseUtcTimestamp(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

/** Human label of a planning run's base case, shared by the sidebar and the map banner. */
export function baseCaseLabel(run: { baseCase: { mode: string; direction: string; frameAt?: string } }): string {
  const { mode, direction, frameAt } = run.baseCase;
  if (mode === 'archive') {
    return `${direction === 'feedIn' ? 'Einspeise-Frame' : 'Starklast-Frame'} ${formatTs(frameAt)}`;
  }
  return direction === 'feedIn' ? 'Synthetischer Worst Case (Einspeisung)' : 'Synthetischer Worst Case (Starklast)';
}

/** Carried share of a collapsing scenario in whole percent, rounded down (the limit is a lower bound). */
export function collapseSharePct(collapse: { carriedFraction: number }): number {
  return Math.floor(collapse.carriedFraction * 100);
}

/** Short headline of a collapsing scenario for the map banner and the result card. */
export function collapseHeadline(collapse: { carriedFraction: number; hasLimitFrame: boolean }): string {
  return collapse.hasLimitFrame
    ? `Spannungskollaps – Netz trägt nur ${collapseSharePct(collapse)} % der Änderung`
    : 'Spannungskollaps – schon ohne Zubau keine Lastfluss-Lösung';
}

/** Direction labels of the Hochlaufpfad (map banner + sidebar). */
export const ROLLOUT_DIRECTION_LABEL: Record<'peakLoad' | 'feedIn' | 'summerPeak', string> = {
  peakLoad: 'Starklast',
  feedIn: 'Einspeisung',
  summerPeak: 'Sommerabend',
};

export const MEASURE_KIND_LABELS: Record<string, string> = {
  cable_reinforcement: 'Kabel',
  fuse_upgrade: 'Sicherung',
  trafo_upgrade: 'Trafo',
  trafo_tap: 'Stufenschalter',
  coupling_switch: 'Trennstelle',
};

const signed = (v: number) => `${v > 0 ? '+' : ''}${v.toLocaleString('de-DE', { maximumFractionDigits: 1 })}`;

/** "→ NAYY 4x240 SE", "→ 315 A", "→ 630 kVA", "+2,5 %" / "rONT 1,030 pu". */
export function measureValueLabel(m: PlanningMeasure): string {
  switch (m.kind) {
    case 'cable_reinforcement': return `→ ${m.cableType ?? '?'}`;
    case 'fuse_upgrade': return `→ ${m.fuseRatingA ?? '?'} A`;
    case 'trafo_upgrade': return `→ ${m.ratedKva ?? '?'} kVA`;
    case 'coupling_switch': return m.isClosed ? '→ geschlossen' : '→ offen';
    case 'trafo_tap':
      return m.voltageSetpointPu != null
        ? `rONT ${m.voltageSetpointPu.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} pu`
        : `${signed(m.tapStepPercent ?? 0)} %`;
    default: return m.kind;
  }
}

/** "Kabel Abgang 3 → NAYY 4x240 SE" — one line per measure for lists and hints. */
export function measureLabel(m: PlanningMeasure, nameOf: (id: string) => string): string {
  return `${MEASURE_KIND_LABELS[m.kind] ?? m.kind} ${nameOf(m.elementId)} ${measureValueLabel(m)}`;
}

/** Label of a remedy violation ("Kabel Abgang 3 112 %", "Unterspannung Haus 4 0,88 pu"). */
export function violationLabel(v: { kind: string; elementId: string; name?: string; value: number; unit?: string }): string {
  const name = v.name ?? v.elementId;
  const pct = (x: number) => `${x.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
  const voltage = (x: number) => v.unit === 'pu'
    ? `${x.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} pu`
    : `${pct(x)} Abweichung`;
  switch (v.kind) {
    case 'cable_loading': return `Kabel ${name} ${pct(v.value)}`;
    case 'fuse_loading': return `Sicherung ${name} ${pct(v.value)}`;
    case 'trafo_loading': return `Trafo ${name} ${pct(v.value)}`;
    case 'undervoltage': return `Unterspannung ${name} ${voltage(v.value)}`;
    case 'overvoltage': return `Überspannung ${name} ${voltage(v.value)}`;
    case 'voltage_rise': return `Spannungsanhebung ${name} ${pct(v.value)}`;
    default: return `${v.kind} ${name}`;
  }
}
